import { useState } from 'react';
import { Alert, Button, Card, Divider, Form, Input, Typography } from 'antd';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuthSession, useLogin } from '@/entities/auth/api/hooks';
import { resolvePostLoginPath } from '@/entities/auth/lib/resolvePostLoginPath';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import s from './Login.module.scss';
import { authErrorMessage } from './utils';

const { Text, Title } = Typography;

const DEMO_ACCOUNTS = [
  { login: 'admin', password: 'admin_password_123', label: 'Администратор' },
  { login: 'ot_specialist', password: 'ot_specialist_password_123', label: 'Специалист по ОТ' },
  { login: 'department_head', password: 'department_head_password_123', label: 'Руководитель' },
  { login: 'master', password: 'master_password_123', label: 'Мастер' },
  { login: 'viewer', password: 'viewer_password_123', label: 'Просмотр' },
  { login: 'employee', password: 'employee_password_123', label: 'Сотрудник' },
] as const;

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending: sessionPending } = useAuthSession();
  const loginMutation = useLogin();
  const [form] = Form.useForm();
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (sessionPending) {
    return <PageSpinner />;
  }

  if (session?.user) {
    return <Navigate to={resolvePostLoginPath(session.user, '/')} replace />;
  }

  const from =
    (location.state as { from?: string } | null)?.from && (location.state as { from?: string }).from !== '/login'
      ? (location.state as { from: string }).from
      : '/';

  const onFinish = (values: { login: string; password: string }) => {
    setSubmitError(null);
    loginMutation.mutate(
      { login: values.login.trim(), password: values.password },
      {
        onSuccess: (data) => {
          navigate(resolvePostLoginPath(data.user, from), { replace: true });
        },
        onError: (err) => {
          setSubmitError(authErrorMessage(err));
        },
      },
    );
  };

  return (
    <div className={s.root}>
      <Card className={s['root__card']} bordered={false}>
        <div className={s['root__brand']}>
          <div className={s['root__brand-text']}>
            <Title level={4} className={s['root__title']}>
              АРМ охраны труда
            </Title>
            <Text type="secondary" className={s['root__sub']}>
              АО «Самарский металлургический завод»
            </Text>
          </div>
        </div>

        <Title level={5} className={s['root__form-title']}>
          Вход в систему
        </Title>

        {submitError ? <Alert type="error" showIcon message={submitError} className={s['root__alert']} /> : null}

        <Form form={form} layout="vertical" className={s['root__form']} onFinish={onFinish} requiredMark={false}>
          <Form.Item label="Логин" name="login" rules={[{ required: true, message: 'Введите логин' }]}>
            <Input size="large" placeholder="например, p.ivanova" autoComplete="username" />
          </Form.Item>
          <Form.Item label="Пароль" name="password" rules={[{ required: true, message: 'Введите пароль' }]}>
            <Input.Password size="large" placeholder="Пароль" autoComplete="current-password" />
          </Form.Item>
          <Form.Item className={s['root__submit-wrap']}>
            <Button type="primary" size="large" htmlType="submit" loading={loginMutation.isPending} block>
              Войти
            </Button>
          </Form.Item>
        </Form>

        <Divider plain className={s['root__demo-divider']}>
          <Text type="secondary" className={s['root__demo-label']}>
            Только для demo-целей. Не использовать в production.
          </Text>
        </Divider>

        <div className={s['root__demo-buttons']}>
          {DEMO_ACCOUNTS.map((acc) => (
            <Button
              key={acc.login}
              size="small"
              className={s['root__demo-btn']}
              loading={loginMutation.isPending}
              onClick={() => {
                setSubmitError(null);
                form.setFieldsValue({ login: acc.login, password: acc.password });
                loginMutation.mutate(
                  { login: acc.login, password: acc.password },
                  {
                    onSuccess: (data) => navigate(resolvePostLoginPath(data.user, from), { replace: true }),
                    onError: (err) => setSubmitError(authErrorMessage(err)),
                  },
                );
              }}
            >
              {acc.label}
            </Button>
          ))}
        </div>
      </Card>
    </div>
  );
}
